import { Button, Tag } from '@ads/components-react';
import { ArrowRightIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import type {
  IndividualPaymentOrderTrackingViewModel,
  IndividualPaymentTraceabilityViewModel,
} from '@/shared/api/generated';
import { Skeleton } from '@/shared/ui/atoms';
import { Drawer } from '@/shared/ui/organisms/Drawer';

import type { PaymentHistoryMode, PaymentHistoryResult } from '../../../types/paymentHistory';
import { EMPTY_VALUE } from '../../../utils/formatCurrency';
import { formatDateTime } from '../../../utils/formatDateTime';

/** Props de `PaymentHistoryDrawer`. */
export interface PaymentHistoryDrawerProps {
  /** Visibilidade do painel. */
  readonly open: boolean;
  /** Modo exibido; `null` com o painel fechado. */
  readonly mode: PaymentHistoryMode | null;
  /** Título do painel — nomeia o escopo e o modo. */
  readonly title: string;
  /** Contexto do escopo: número da parcela ou aluno do contrato. */
  readonly subtitle?: string | undefined;
  /** Dica do estado vazio — precisa dizer de qual escopo se trata. */
  readonly emptyHint: string;
  /** Resultado do hook de histórico do escopo. */
  readonly history: PaymentHistoryResult;
  readonly onClose: () => void;
}

/** Cartão de uma linha do histórico — cabeçalho com metadados + corpo variável. */
function EntryCard({
  title,
  date,
  user,
  children,
}: {
  title: string;
  date: string;
  user: string | null | undefined;
  children: React.ReactNode;
}) {
  const { t } = useTranslation('individualPayment');

  return (
    <li className="flex flex-col gap-nano rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-mini">
      <div className="flex flex-wrap items-center gap-nano">
        <span className="ts-subtitle-xxs text-ctx-content-title">{title}</span>
        <span className="grow" />
        <span className="ts-caption-xxs tabular-nums text-ctx-content-placeholder">{date}</span>
      </div>
      {user && (
        <span className="ts-caption-xxs text-ctx-content-placeholder">
          {t('parcels.history.changedBy', { user })}
        </span>
      )}
      {children}
    </li>
  );
}

/** Par rótulo-valor usado nos corpos dos cartões. */
function Row({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex gap-nano">
      <span className="w-[5.5rem] shrink-0 ts-caption-xxs text-ctx-content-placeholder">
        {label}
      </span>
      <span className="min-w-none break-all ts-caption-xxs text-ctx-content-base">{value}</span>
    </div>
  );
}

function TraceabilityEntry({ entry }: { entry: IndividualPaymentTraceabilityViewModel }) {
  const { t } = useTranslation('individualPayment');

  return (
    <EntryCard
      title={entry.fieldName ?? entry.type ?? EMPTY_VALUE}
      date={formatDateTime(entry.date)}
      user={entry.username}
    >
      {/* Antigo → novo lado a lado: o valor de uma alteração está na diferença,
          e empilhar os dois em linhas separadas obriga a comparar de memória. */}
      <div className="flex flex-wrap items-center gap-nano">
        <span className="rounded-sm bg-base-gray-100 px-nano py-quark ts-caption-xxs text-ctx-content-base line-through">
          {entry.oldValue || EMPTY_VALUE}
        </span>
        <ArrowRightIcon className="h-mini w-mini shrink-0 text-ctx-content-placeholder" />
        <span className="rounded-sm bg-base-success-100 px-nano py-quark ts-caption-xxs text-base-success-800">
          {entry.newValue || EMPTY_VALUE}
        </span>
      </div>
      {entry.entity && (
        <Row
          label={t('parcels.history.entity')}
          value={entry.entityId ? `${entry.entity} #${entry.entityId}` : entry.entity}
        />
      )}
    </EntryCard>
  );
}

function OrderTrackingEntry({ entry }: { entry: IndividualPaymentOrderTrackingViewModel }) {
  const { t } = useTranslation('individualPayment');

  return (
    <EntryCard
      title={entry.typeName ?? EMPTY_VALUE}
      date={formatDateTime(entry.dateLog)}
      user={entry.userName}
    >
      <Row label={t('parcels.history.order')} value={entry.orderId} />
      <Row label={t('parcels.history.message')} value={entry.message} />
      <Row label={t('parcels.history.result')} value={entry.result} />
      {entry.payload && (
        <details className="mt-quark">
          <summary className="cursor-pointer ts-caption-xxs text-ctx-content-placeholder">
            {t('parcels.history.payload')}
          </summary>
          {/* `break-all` + `max-h`: o payload é um blob de integração que, solto,
              estica o drawer indefinidamente. */}
          <pre className="mt-quark max-h-[12rem] overflow-auto whitespace-pre-wrap break-all rounded-sm bg-base-gray-100 p-nano ts-caption-xxxs text-ctx-content-base">
            {entry.payload}
          </pre>
        </details>
      )}
    </EntryCard>
  );
}

/**
 * Painel lateral com um histórico de pagamento individual, genérico ao escopo.
 *
 * Serve os dois modos de leitura — auditoria campo a campo (`traceability`) e
 * rastreio de pedidos (`orderTracking`) — tanto no escopo da parcela quanto no do
 * plano financeiro. Um painel só, porque os quatro endpoints devolvem os mesmos
 * dois ViewModels e as duas listas são cronológicas, de leitura, e nunca aparecem
 * juntas.
 *
 * Este componente é apresentação pura: quem escolhe o escopo são os wrappers
 * (`ParcelHistoryDrawer`, `PlanHistoryDrawer`), que chamam o hook e resolvem os
 * textos. O `Drawer` compartilhado cuida do portal, do Escape, do overlay e de
 * desmontar apenas depois da animação de saída.
 */
export function PaymentHistoryDrawer({
  open,
  mode,
  title,
  subtitle,
  emptyHint,
  history,
  onClose,
}: PaymentHistoryDrawerProps) {
  const { t } = useTranslation('individualPayment');
  const { traceability, orderTracking, isLoading, isError, refetch } = history;

  const entries = mode === 'orderTracking' ? orderTracking : traceability;

  return (
    <Drawer open={open} onClose={onClose} title={title} subtitle={subtitle}>
      {isLoading ? (
        <ul className="flex flex-col gap-micro">
          {Array.from({ length: 4 }, (_, index) => `history-skeleton-${index}`).map(key => (
            <li
              key={key}
              className="flex flex-col gap-nano rounded-sm border-hairline border-ctx-layout-border p-mini"
            >
              <Skeleton className="h-xs w-[12rem] rounded-sm" />
              <Skeleton className="h-mini w-[8rem] rounded-sm" />
              <Skeleton className="h-xs w-[16rem] rounded-sm" />
            </li>
          ))}
        </ul>
      ) : isError ? (
        <div className="flex flex-col items-center gap-micro p-xl">
          <span className="flex items-center justify-center rounded-pill bg-base-danger-100 p-micro">
            <ExclamationTriangleIcon className="h-xxs w-xxs text-base-danger-700" />
          </span>
          <p className="ts-heading-xs text-ctx-content-title">{t('parcels.history.error')}</p>
          <Button hierarchy="secondary" size="md" onClick={() => refetch()}>
            {t('parcels.table.retry')}
          </Button>
        </div>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center gap-micro p-xl">
          <span className="flex items-center justify-center rounded-pill bg-ctx-layout-body p-micro">
            <ClockIcon className="h-xxs w-xxs text-ctx-content-placeholder" />
          </span>
          <p className="ts-heading-xs text-ctx-content-title">{t('parcels.history.empty')}</p>
          <p className="ts-paragraph-xs text-ctx-content-placeholder">{emptyHint}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-micro">
          <div className="flex items-center gap-nano">
            <Tag
              label={t('parcels.history.count', { count: entries.length })}
              size="sm"
              status="default"
              custom={{ text: 'text-nowrap' }}
            />
          </div>
          <ul className="flex flex-col gap-micro">
            {mode === 'orderTracking'
              ? orderTracking.map((entry, index) => (
                  <OrderTrackingEntry key={entry.entityId ?? `order-${index}`} entry={entry} />
                ))
              : traceability.map((entry, index) => (
                  <TraceabilityEntry key={entry.id ?? `trace-${index}`} entry={entry} />
                ))}
          </ul>
        </div>
      )}
    </Drawer>
  );
}
